import React, { useState, useEffect, useCallback } from 'react';
import type { PlaybookTransitionFull, PlaybookStatusFull } from '../../types/playbook.types';

interface ApprovalChainBuilderProps {
  transition: PlaybookTransitionFull;
  statuses: PlaybookStatusFull[];
  onUpdate: (transitionId: string, data: Partial<PlaybookTransitionFull>) => Promise<void>;
}

interface ChainStep {
  step_order: number;
  required_role: string;
  is_optional: boolean;
  sla_hours?: number;
}

const ApprovalChainBuilder: React.FC<ApprovalChainBuilderProps> = ({ transition, statuses, onUpdate }) => {
  const [steps, setSteps] = useState<ChainStep[]>([]);
  const [onRejection, setOnRejection] = useState(transition.on_rejection ?? 'block');
  const [rejectionStatusId, setRejectionStatusId] = useState(transition.rejection_target_status_id ?? '');

  useEffect(() => {
    const existing = (transition.approval_chain ?? []) as unknown as ChainStep[];
    setSteps(existing.length > 0 ? existing : [
      { step_order: 1, required_role: 'attorney', is_optional: false },
      { step_order: 2, required_role: 'supervising_attorney', is_optional: false },
    ]);
    setOnRejection(transition.on_rejection ?? 'block');
    setRejectionStatusId(transition.rejection_target_status_id ?? '');
  }, [transition.id]);

  const save = useCallback(() => {
    onUpdate(transition.id, {
      approval_chain: steps as any[],
      on_rejection: onRejection as any,
      rejection_target_status_id: onRejection === 'revert_to_status' ? rejectionStatusId || null : null,
    });
  }, [transition.id, steps, onRejection, rejectionStatusId, onUpdate]);

  const addStep = () => {
    setSteps((prev) => [
      ...prev,
      { step_order: prev.length + 1, required_role: '', is_optional: false },
    ]);
  };

  const removeStep = (idx: number) => {
    setSteps((prev) => prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, step_order: i + 1 })));
  };

  const updateStep = (idx: number, data: Partial<ChainStep>) => {
    setSteps((prev) => prev.map((s, i) => i === idx ? { ...s, ...data } : s));
  };

  const moveStep = (idx: number, dir: -1 | 1) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= steps.length) return;
    const next = [...steps];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setSteps(next.map((s, i) => ({ ...s, step_order: i + 1 })));
  };

  const noSlaWarning = steps.some((s) => !s.sla_hours);
  const tooFewSteps = steps.length < 2;

  const inputClass = 'w-full rounded-md border border-gray-300 px-2 py-1 text-xs dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300';
  const labelClass = 'block text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase mb-0.5';

  return (
    <div className="space-y-3" style={{ animation: 'fadeSlideIn 200ms ease-out' }}>
      <h5 className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Approval Chain</h5>

      {/* Stepper preview */}
      <div className="flex items-center gap-1 overflow-x-auto py-1">
        {steps.map((s, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center justify-center h-7 min-w-[60px] rounded-full px-2 text-[9px] font-medium border ${
              s.is_optional
                ? 'border-dashed border-gray-300 text-gray-400 dark:border-gray-600'
                : 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-600 dark:bg-amber-900/20 dark:text-amber-300'
            }`}>
              {s.required_role || `Step ${s.step_order}`}
            </div>
            {i < steps.length - 1 && (
              <span className="text-[10px] text-gray-300 dark:text-gray-600">→</span>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Warnings */}
      {tooFewSteps && (
        <div className="rounded border border-amber-200 bg-amber-50/50 px-2 py-1 text-[9px] text-amber-600 dark:border-amber-700 dark:bg-amber-900/10 dark:text-amber-400">
          Minimum 2 approval steps recommended
        </div>
      )}
      {!tooFewSteps && noSlaWarning && (
        <div className="rounded border border-amber-200 bg-amber-50/50 px-2 py-1 text-[9px] text-amber-600 dark:border-amber-700 dark:bg-amber-900/10 dark:text-amber-400">
          Some steps have no SLA — approvals may stall without escalation
        </div>
      )}

      {/* Step editor */}
      <div className="space-y-2">
        {steps.map((s, idx) => (
          <div key={idx} className="rounded border border-gray-200 dark:border-gray-700 p-2 space-y-1.5">
            <div className="flex items-center justify-between">
              <span className="text-[9px] font-medium text-gray-500 dark:text-gray-400">Step {s.step_order}</span>
              <div className="flex gap-1">
                <button type="button" onClick={() => moveStep(idx, -1)} disabled={idx === 0} className="text-[9px] text-gray-400 hover:text-gray-600 disabled:opacity-30">↑</button>
                <button type="button" onClick={() => moveStep(idx, 1)} disabled={idx === steps.length - 1} className="text-[9px] text-gray-400 hover:text-gray-600 disabled:opacity-30">↓</button>
                <button type="button" onClick={() => removeStep(idx)} className="text-[9px] text-rose-500 hover:text-rose-700">×</button>
              </div>
            </div>
            <div>
              <label className={labelClass}>Role</label>
              <input type="text" className={inputClass} value={s.required_role} onChange={(e) => updateStep(idx, { required_role: e.target.value })} placeholder="e.g. supervising_attorney" />
            </div>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-[10px] text-gray-600 dark:text-gray-400">
                <input type="checkbox" checked={s.is_optional} onChange={(e) => updateStep(idx, { is_optional: e.target.checked })} className="rounded border-gray-300 dark:border-gray-600" />
                Optional
              </label>
              <div className="flex-1">
                <input type="number" className={inputClass} value={s.sla_hours ?? ''} onChange={(e) => updateStep(idx, { sla_hours: e.target.value ? Number(e.target.value) : undefined })} placeholder="SLA hours" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <button type="button" onClick={addStep} className="w-full rounded border border-dashed border-gray-300 py-1 text-[10px] text-gray-500 hover:border-lex-accent hover:text-lex-accent dark:border-gray-600">
        + Add Step
      </button>

      {/* On-rejection behavior */}
      <div>
        <label className={labelClass}>On Rejection</label>
        <select className={inputClass} value={onRejection} onChange={(e) => setOnRejection(e.target.value as any)}>
          <option value="block">Block</option>
          <option value="revert_to_previous">Revert to Previous Status</option>
          <option value="revert_to_status">Revert to Specific Status</option>
        </select>
      </div>

      {onRejection === 'revert_to_status' && (
        <div>
          <label className={labelClass}>Revert To</label>
          <select className={inputClass} value={rejectionStatusId} onChange={(e) => setRejectionStatusId(e.target.value)}>
            <option value="">Select status...</option>
            {statuses.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
      )}

      <button type="button" onClick={save} className="w-full rounded bg-lex-accent px-3 py-1.5 text-xs font-medium text-lex-accent-contrast hover:bg-lex-accent-hover">
        Save Approval Chain
      </button>
    </div>
  );
};

export default ApprovalChainBuilder;
